import type { Response, ErrorResponse, SuccessResponse } from "@/api";

const abortExceptions = [
  "AbortError",
  "DOMException",
  "TypeError: Failed to fetch",
];

export async function handleResponseAsync<T1>(
  response: Response<T1> | null,
  onSuccess: (data: T1) => void | Promise<void>,
  componentName: string,
  functionName: string,
  onError?: (
    error: string,
    response: ErrorResponse | null
  ) => void | Promise<void>
) {
  try {
    if (!response) {
      console.error(`${componentName}: ${functionName}(): response is null`);
      if (onError) await onError("Response is null", null);
      return;
    }

    if (!response.success) {
      const errorResponse = response as ErrorResponse;
      // Ignore abort request errors
      if (
        abortExceptions.includes(errorResponse.error.name) ||
        (errorResponse.error.stack &&
          abortExceptions.includes(errorResponse.error.stack.split("\n")[0]))
      ) {
        console.debug(
          `${componentName}: ${functionName}(): request abort exception handled`
        );
        return;
      }

      console.error(
        `${componentName}: ${functionName}(): resp.error =`,
        errorResponse.error
      );
      if (onError) await onError(errorResponse.error.toString(), errorResponse);
      return;
    }
    console.debug(`${componentName}: ${functionName}(), response:`, response);
    await onSuccess((response as SuccessResponse<T1>).response);
  } catch (e: any) {
    console.error(`${componentName}: ${functionName}(): catch error =`, e);
    if (onError) await onError(e.toString(), response as ErrorResponse);
    return;
  }
}

export async function handleResponsePromiseAsync<T1>(
  responsePromise: Promise<Response<T1> | null>,
  onSuccess: (data: T1) => void | Promise<void>,
  componentName: string,
  functionName: string,
  onError?: (
    error: string,
    response: ErrorResponse | null
  ) => void | Promise<void>
) {
  try {
    const response = await responsePromise;
    return await handleResponseAsync(
      response,
      onSuccess,
      componentName,
      functionName,
      onError
    );
  } catch (e: any) {
    console.error(`${componentName}: ${functionName}(): catch error =`, e);
    if (onError) await onError(e.toString(), null);
    return;
  }
}
